import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Layout } from '@abyss/web/ui/Layout';
import { Text } from '@abyss/web/ui/Text';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag/useFeatureFlag';
import { useLocationCoordinate } from '../../../../hooks/useLocationCoordinate';
import { useCareTeamStore } from '../../../../store/useCareTeamStore';
import { adobeLinkTrackEvent } from '../../../AdobeTagging/adobeLinkTrackEvent';
import { FindMyLocationButton } from './FindMyLocationButton';
import {
  DropdownContentContainer,
  DropdownPositionContainer,
  hiddenTextStyle,
} from './LocationDropdown.styled';
import { LocationDropdownSkeleton } from './LocationDropdownSkeleton';
import { LocationSuggestionStack } from './LocationSuggestionStack';

type Props = {
  onClose: () => void;
  activeSuggestion: number;
  isCurrentLocation?: boolean;
  locationInputVal: string;
  locationSuggestions?: object[];
  isLocationLoading: boolean;
  setIsCurrentLocation?: (val: boolean) => void;
  setSelectedLocation: (any) => void;
  setLocationInputVal: (val: string) => void;
  setLocationSuggestions: (value: object[]) => void;
  cssProps: object;
  id?: string;
  selectedLocationLabel?: string;
};

export const LocationDropdown = ({
  onClose,
  activeSuggestion,
  locationSuggestions = [],
  isLocationLoading,
  setSelectedLocation,
  locationInputVal,
  setLocationInputVal,
  isCurrentLocation,
  setIsCurrentLocation,
  setLocationSuggestions,
  cssProps,
  id,
  selectedLocationLabel,
}: Props) => {
  const { t } = useTranslation();
  const locationForAnalytics: string = 'change search location';
  const { setShowMaximumProviderLimitBanner } = useCareTeamStore((state) => ({
    setShowMaximumProviderLimitBanner: state.setShowMaximumProviderLimitBanner,
  }));

  const [, getLocationCoordinate] = useLocationCoordinate({
    onCompleted: (result) => {
      result;
    },
    onError: () => {},
  });
  const [isAWSLocationSearchEnabled] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.SWITCH_TO_AWS_LOCATION_SEARCH,
  ]);
  const handleLocationItemClick = async (location: {
    place_name: string;
    id: string;
  }) => {
    setShowMaximumProviderLimitBanner(false);
    adobeLinkTrackEvent({
      name: 'suggested location',
      location: `body:${locationForAnalytics}`,
      type: 'internal',
    });
    if (isAWSLocationSearchEnabled) {
      const { data } = await getLocationCoordinate({
        variables: {
          placeID: location.id,
        },
      });
      setSelectedLocation(data?.locationCoordinate?.features[0]);
      setLocationInputVal(data?.locationCoordinate?.features[0]?.place_name);
    } else {
      setSelectedLocation(location);
      setLocationInputVal(location?.place_name);
    }

    onClose();
  };

  return (
    <DropdownPositionContainer data-testid="location-dropdown-container">
      <DropdownContentContainer cssProps={cssProps}>
        <Layout.Stack
          data-testid="location-suggestions-dropdown-container"
          grow
          space={15}
        >
          <Text color="$gray7">
            {t('LOCATION_SEARCH.HEADER_DECEMBER_RELEASE')}
            <span
              id={id}
              style={{
                display: 'none',
              }}
            >
              {t('LOCATION_SEARCH.HEADER_DECEMBER_RELEASE')}
            </span>
          </Text>
          <Layout.Stack alignItems="left" space={12}>
            {isLocationLoading ? (
              <LocationDropdownSkeleton testId="location-dropdown-container-skeleton" />
            ) : !isCurrentLocation &&
              locationSuggestions?.length === 0 &&
              locationInputVal.trim().length > 0 &&
              locationInputVal !== selectedLocationLabel ? (
              <Layout.Group
                alignItems="top"
                data-testid="location-invalid-suggestion"
              >
                <IconMaterial color="$error1" icon="error" />
                <Text color="$error1" role="alert">
                  <Text css={hiddenTextStyle}>Error: </Text>
                  {t('LOCATION_SEARCH.ERROR')}
                </Text>
              </Layout.Group>
            ) : locationInputVal.trim().length > 0 ? (
              <LocationSuggestionStack
                activeSuggestion={activeSuggestion}
                onSuggestionClick={handleLocationItemClick}
                suggestions={locationSuggestions}
              />
            ) : null}
            <FindMyLocationButton
              index={locationSuggestions.length}
              onClose={onClose}
              setIsCurrentLocation={setIsCurrentLocation}
              setLocationSuggestions={setLocationSuggestions}
            />
          </Layout.Stack>
        </Layout.Stack>
      </DropdownContentContainer>
    </DropdownPositionContainer>
  );
};
